import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { HARDWARE_TIERS } from "@/lib/hardware";
import { Check, PackageCheck, Wrench, ArrowRight } from "lucide-react";

export default function ProPlus() {
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const [tier, setTier] = useState("founding"); // founding | pro

  return (
    <section id="proplus" data-testid="proplus-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* header */}
        <div className="max-w-3xl">
          <Reveal>
            <p className="overline text-[#F5A623]">DriveAssure Pro+ · Coming in 2027</p>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              Linked dashcam integration. Coming in 2027.
            </h2>
            <p className="mt-6 text-lg text-[#2B2B2B]/65 leading-relaxed">
              Ahead of South Australia's 2027 reforms, DriveAssure Pro+ will link a compliant dual-channel
              dashcam directly to the app — with professional hardwired installation included in one monthly
              plan. Here's a preview of the bundles we're building.
            </p>
          </Reveal>

          {/* brand differentiator */}
          <Reveal delay={0.08}>
            <div className="mt-7 flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#F5A623]/12 px-4 py-2 text-[#B57608] font-medium">
                <span className="w-2 h-2 rounded-full bg-[#F5A623]" /> BlackVue — the premium connected benchmark
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#2BBFB3]/12 px-4 py-2 text-[#1FA097] font-medium">
                <span className="w-2 h-2 rounded-full bg-[#2BBFB3]" /> DriveAssure Value — our own compliant units
              </span>
            </div>
          </Reveal>
        </div>

        {/* subscription tier pill */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center gap-3">
            <span className="text-sm font-medium text-[#2B2B2B]/50">Your plan:</span>
            <div className="inline-flex items-center rounded-full border border-[#E5E5E5] bg-white p-1" data-testid="tier-pill">
              {[["founding", "Founding Instructor"], ["pro", "Pro"]].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`tier-${key}`}
                  onClick={() => setTier(key)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                    tier === key ? "bg-[#2BBFB3] text-[#0f1211]" : "text-[#2B2B2B]/60 hover:text-[#2B2B2B]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        {/* billing toggle */}
        <Reveal delay={0.12}>
          <div className="mt-4 inline-flex items-center rounded-full border border-[#E5E5E5] bg-[#F7F7F7] p-1" data-testid="billing-toggle">
            <button
              data-testid="billing-monthly"
              onClick={() => setBilling("monthly")}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                billing === "monthly" ? "bg-[#2B2B2B] text-white" : "text-[#2B2B2B]/60 hover:text-[#2B2B2B]"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setBilling("annual")}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                billing === "annual" ? "bg-[#2B2B2B] text-white" : "text-[#2B2B2B]/60 hover:text-[#2B2B2B]"
              }`}
            >
              Annual prepay
              <span className="rounded-full bg-[#2BBFB3] px-2 py-0.5 text-[10px] font-bold text-[#0f1211]">2 months free</span>
            </button>
          </div>
        </Reveal>

        {/* tier cards */}
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {HARDWARE_TIERS.map((t, i) => {
            const premium = t.brandType === "premium";
            const p = t.prices[tier];
            return (
              <Reveal key={t.id} delay={i * 0.06}>
                <div
                  data-testid={`hardware-tier-${t.id}`}
                  className={`relative h-full flex flex-col rounded-2xl border bg-white p-6 transition-transform duration-300 hover:-translate-y-1 ${
                    t.popular ? "border-[#2BBFB3] ring-1 ring-[#2BBFB3]" : "border-[#E5E5E5]"
                  }`}
                >
                  {t.popular && (
                    <span
                      data-testid="hardware-popular-badge"
                      className="absolute -top-3 left-6 rounded-full bg-[#F5A623] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#2B2B2B]"
                    >
                      Featured
                    </span>
                  )}

                  {/* brand chip */}
                  <span
                    className={`self-start inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                      premium ? "bg-[#F5A623]/12 text-[#B57608]" : "bg-[#2BBFB3]/12 text-[#1FA097]"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${premium ? "bg-[#F5A623]" : "bg-[#2BBFB3]"}`} />
                    {t.brand}
                  </span>

                  <h3 className="mt-4 font-display font-bold text-xl text-[#2B2B2B]">{t.tier}</h3>
                  <p className="mt-1 text-sm font-medium text-[#2B2B2B]/50">{t.model}</p>

                  {/* hero price */}
                  <div className="mt-6">
                    {billing === "monthly" ? (
                      <>
                        <div className="flex items-end gap-1">
                          <span className="font-display font-extrabold text-5xl leading-none text-[#2B2B2B]">
                            {p.monthly}
                          </span>
                          <span className="mb-1 text-sm text-[#2B2B2B]/50">/month</span>
                        </div>
                        <p className="mt-2 text-xs text-[#2B2B2B]/45">or {p.annual}/year prepaid</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-end gap-1">
                          <span className="font-display font-extrabold text-5xl leading-none text-[#2B2B2B]">
                            {p.annual}
                          </span>
                          <span className="mb-1 text-sm text-[#2B2B2B]/50">/year</span>
                        </div>
                        <p className="mt-2 text-xs text-[#2B2B2B]/45">= {p.eff}/mo effective</p>
                      </>
                    )}
                  </div>

                  {/* included badge */}
                  <div className="mt-5 flex items-center gap-2 rounded-xl bg-[#F7F7F7] px-3 py-2.5 text-xs font-medium text-[#2B2B2B]/70">
                    <PackageCheck size={15} className="text-[#1FA097]" />
                    Dashcam + install included
                  </div>

                  <p className="mt-5 text-sm text-[#2B2B2B]/60 leading-relaxed">{t.tagline}</p>

                  <ul className="mt-5 space-y-2.5 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check size={15} className="mt-0.5 shrink-0 text-[#2BBFB3]" />
                        <span className="text-[#2B2B2B]/75">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <span
                    data-testid={`hardware-cta-${t.id}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold bg-[#2B2B2B]/[0.06] text-[#2B2B2B]/55 cursor-default"
                  >
                    Coming in 2027
                  </span>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* install note + app-only path */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 rounded-2xl border border-[#E5E5E5] bg-[#F7F7F7] p-6">
            <p className="flex items-start gap-3 text-sm text-[#2B2B2B]/60 max-w-xl">
              <Wrench size={18} className="mt-0.5 shrink-0 text-[#1FA097]" />
              When Pro+ launches in 2027, every plan will include professional hardwired installation at no
              extra cost, carried out by qualified auto electricians at approved South Australian locations.
            </p>
            <a
              href="#pricing"
              data-testid="app-only-link"
              className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#2B2B2B]/20 px-6 py-3 text-sm font-semibold text-[#2B2B2B] hover:border-[#2BBFB3] hover:text-[#1FA097] transition-colors"
            >
              Available today — see app plans
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
